import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, LoaderCircle, Share, Heart } from 'lucide-react';
import Confetti from 'react-confetti';

import { config, callApi } from './NetUtils';
import { addUserReport } from './reportStorageUtils';

const TurnstileWidget = ({ onTokenChange, theme }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !window.turnstile) return;
    const widgetId = window.turnstile.render(ref.current, {
      sitekey: '0x4AAAAAABiyrBzrrCvNc0Yb', // IMPORTANT: Replace with your actual Site Key
      callback: (token) => onTokenChange(token),
      theme: theme
    });
    return () => window.turnstile.remove(widgetId);
  }, [onTokenChange, theme]);
  return <div ref={ref} />;
};

const ConfettiAnimation = () => {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return <Confetti width={size.width} height={size.height} recycle={false} numberOfPieces={500} />;
};

const ReportForm = ({ theme }) => {
  const [companyName, setCompanyName] = useState('');
  const [aiUse, setAiUse] = useState('');
  const [impact, setImpact] = useState('');
  const [url, setUrl] = useState('');
  const [vote, setVote] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [formMessage, setFormMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showDonationPrompt, setShowDonationPrompt] = useState(false);
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (companyName.length < 3) {
      setCompanySuggestions([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      return;
    }
    const timeout = setTimeout(async () => {
      const response = await callApi(config.DATA_API_URL, `/search-companies?q=${encodeURIComponent(companyName)}`);
      if (response.success) {
        setCompanySuggestions(response.data);
        setShowDropdown(response.data.length > 0);
        setHighlightedIndex(-1);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [companyName]);

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < companySuggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : companySuggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < companySuggestions.length) {
        e.preventDefault();
        selectCompany(companySuggestions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyName || !aiUse || !impact || !vote) {
      setFormMessage({ type: 'error', text: 'Please fill out all fields and select a sentiment.' });
      return;
    }
    if (!turnstileToken) {
      setFormMessage({ type: 'error', text: 'Please complete the CAPTCHA challenge.' });
      return;
    }
    setIsSubmitting(true);
    setFormMessage(null);

    const reportDetails = { companyName, aiUse, impact, vote, turnstileToken };
    if (url && url.trim().length > 0) {
      reportDetails.url = url.trim();
    }
    const response = await callApi(config.DATA_API_URL, '/reports', 'POST', reportDetails);
    if (response.success) {
      // Store report ID and secret in local storage
      const reportId = response.data.reportId;
      const reportSecret = response.data.reportSecret;
      if (reportId && reportSecret) {
        addUserReport(reportId, reportSecret);
      }
      setFormMessage({ type: 'success', text: `Report submitted. Thank you!` });
      setShowConfetti(true);
      // Show donation prompt after confetti animation (3 seconds)
      setTimeout(() => {
        setShowConfetti(false);
        setShowDonationPrompt(true);
      }, 3000);
    } else {
      setFormMessage({ type: 'error', text: response.error });
    }
    setIsSubmitting(false);
  };

  const selectCompany = (name) => {
    setCompanyName(name);
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Report on AI Usage',
      text: 'Help create transparency around AI in the workplace by submitting an anonymous report.',
      url: window.location.href,
    };
    try {
      await navigator.share(shareData);
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        alert('Could not open share dialog.');
      }
    }
  };

  return (
    <div className="relative max-w-2xl mx-auto">
      {showConfetti && (
        <div className="fixed inset-0 w-screen h-screen pointer-events-none z-50">
          <ConfettiAnimation />
        </div>
      )}
      <div className="relative z-10 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border dark:border-gray-700">
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <h2 className="text-3xl font-bold flex-1 text-center">Report Anonymously</h2>
            <button onClick={handleShare} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0">
              <Share className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <p className="text-center">Your vote updates the company's score.</p>
        </div>
        <div className="flex justify-center mb-4"><div className="w-[300px] flex justify-center"><TurnstileWidget onTokenChange={setTurnstileToken} theme={theme} /></div></div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <label htmlFor="companyName" className="block text-lg font-medium">Company Name</label>
            <input
              id="companyName"
              type="text"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setShowDropdown(true);
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
              className="mt-1 block w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600"
              required
              autoComplete="off"
            />
            {showDropdown && companySuggestions.length > 0 &&
              !(companySuggestions.length === 1 && companySuggestions[0].toLowerCase() === companyName.toLowerCase()) && (
                <div className={`absolute z-50 w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto`}>
                  {companySuggestions.map((name, index) => {
                    const lowerName = name.toLowerCase();
                    const lowerInput = companyName.toLowerCase();
                    const startIndex = lowerName.indexOf(lowerInput);
                    let beforeMatch = name, matchText = "", afterMatch = "";
                    if (startIndex !== -1 && lowerInput.length > 0) {
                      beforeMatch = name.slice(0, startIndex);
                      matchText = name.slice(startIndex, startIndex + lowerInput.length);
                      afterMatch = name.slice(startIndex + lowerInput.length);
                    }
                    return (
                      <div key={name} onClick={() => selectCompany(name)} className={`relative px-4 py-2 cursor-pointer group`}>
                        <span className={`absolute left-0 top-0 h-full w-2 rounded-r ${highlightedIndex === index ? 'bg-blue-600' : 'group-hover:bg-blue-400'}`}></span>
                        <span className="pl-3 block">{beforeMatch}<span className="font-semibold text-blue-600">{matchText}</span>{afterMatch}</span>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
          <div><label htmlFor="aiUse" className="block text-lg font-medium">What is the AI being used for?</label><textarea id="aiUse" rows="3" value={aiUse} onChange={(e) => setAiUse(e.target.value)} className="mt-1 block w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600" required /></div>
          <div><label htmlFor="impact" className="block text-lg font-medium">How has this AI impacted human workers?</label><textarea id="impact" rows="5" value={impact} onChange={(e) => setImpact(e.target.value)} className="mt-1 block w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600" required /></div>
          <div>
            <label htmlFor="url" className="block text-lg font-medium">Supporting URL</label>
            <input
              id="url"
              type="url"
              placeholder="https://example.com/article"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-1 block w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">If provided, this link will be shown with your report.</p>
          </div>
          <div className="border-t dark:border-gray-600 pt-6 space-y-3">
            <label className="block text-lg font-medium text-center">What is the overall sentiment?</label>
            <div className="flex justify-center">
                <div className="flex justify-center gap-1 w-[300px]">
                    <button type="button" onClick={() => setVote(1)} className={`flex-1 flex justify-center items-center gap-2 text-lg font-semibold py-3 px-6 rounded-lg transition-transform duration-200 ${vote === 1 ? 'bg-green-600 text-white scale-110' : 'bg-green-100 dark:bg-green-800/50 hover:bg-green-200'}`}><ThumbsUp /> Positive</button>
                    <button type="button" onClick={() => setVote(-1)} className={`flex-1 flex justify-center items-center gap-2 text-lg font-semibold py-3 px-6 rounded-lg transition-transform duration-200 ${vote === -1 ? 'bg-red-600 text-white scale-110' : 'bg-red-100 dark:bg-red-800/50 hover:bg-red-200'}`}><ThumbsDown /> Negative</button>
                </div>
            </div>
          </div>
          <div className="flex justify-center">
            <button type="submit" disabled={isSubmitting} className="w-[300px] flex justify-center items-center py-3 px-4 rounded-lg shadow-lg text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400">{isSubmitting ? <><LoaderCircle className="animate-spin mr-2" /> Submitting...</> : 'Submit Anonymously'}</button>
          </div>
          {formMessage && <p className={`text-center font-semibold ${formMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>{formMessage.text}</p>}
        </form>
      </div>

      {/* Donation Prompt Modal - appears after confetti */}
      {showDonationPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl max-w-md text-center">
            <Heart className="mx-auto w-16 h-16 text-pink-600 dark:text-pink-400 mb-4" />
            <h2 className="text-2xl font-bold mb-4">Thank You for Your Report!</h2>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Would you like to support our mission with a donation? Your contribution helps keep this platform free and transparent.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  // Navigate to homepage first
                  navigate('/');
                  // Then open donation link after a brief delay
                  setTimeout(() => {
                    window.open('https://donate.stripe.com/test_dRmaEQ4Sj1oc0no3OO18c00', '_blank', 'noopener,noreferrer');
                  }, 300);
                }}
                className="inline-flex items-center justify-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 shadow-lg"
              >
                <Heart className="w-5 h-5" />
                Yes, I'd like to donate
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                No thanks, take me home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportForm;
